import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { ButtonLink } from '../components/Button';
import { Icon } from '../components/Icon';
import { EmptyBoard } from '../components/illustrations';
import { Paper } from '../components/Paper';
import { EmptyState, ErrorNotice, Loading } from '../components/States';
import { Composer } from '../features/feed/Composer';
import { PostCard } from '../features/posts/PostCard';
import { Replies } from '../features/posts/Replies';
import { getPost } from '../lib/api';
import { errorCode } from '../lib/errors';
import styles from './Page.module.css';

export function Thread() {
  const { postId = '' } = useParams();
  const [editing, setEditing] = useState(false);
  const post = useQuery({ queryKey: ['post', postId], queryFn: () => getPost(postId) });

  const back = (
    <Link to="/" className={styles.back}>
      <Icon name="back" size={18} />
      Board
    </Link>
  );

  if (post.isPending) return <Loading label="Loading note" />;
  if (post.isError) {
    if (errorCode(post.error) === 'not_found') {
      return (
        <div className={styles.stack}>
          {back}
          <Paper>
            <EmptyState
              art={<EmptyBoard />}
              title="This note isn't on your board"
              actions={
                <ButtonLink to="/" variant="primary">
                  Back to the board
                </ButtonLink>
              }
            >
              It may have been deleted, or it was posted by someone outside your area.
            </EmptyState>
          </Paper>
        </div>
      );
    }
    return <ErrorNotice error={post.error} onRetry={() => post.refetch()} />;
  }

  const p = post.data;
  const hidden = p.isDeleted || p.isRemoved;

  return (
    <div className={styles.stack}>
      {back}
      {editing ? (
        <Composer
          editing={{ id: p.id, body: p.body ?? '', category: p.category }}
          onDone={() => setEditing(false)}
          onCancel={() => setEditing(false)}
        />
      ) : (
        <PostCard post={p} variant="thread" onEdit={() => setEditing(true)} />
      )}
      <Replies postId={p.id} canReply={!hidden} />
    </div>
  );
}
